// Models
import { getCustomFieldDefinitionId, isCustomFieldColumn, toCustomFieldColumnId } from "../../../../utilities/CustomFieldDefinitionUtilities";
// Utilities
import { getDiff } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import { calculateOrderHintFromExpression } from "@ms/uno-utilities/lib/local/OrderHintUtilities";
import { generateOrderHintBetween } from "@ms/uno-utilities/lib/local/OrdinalUtilities";
import isEmpty from "lodash/isEmpty";
import { isConditionalColoringColumnId, isConditionalColoringOperator } from "./ConditionalColoringUtilities";
/** Converts a service rule resource to the client rule model. */ export function deserializeConditionalColoringRule(resource, entityName) {
    return {
        id: resource.id,
        version: resource["@etag"],
        orderHint: resource.orderHint,
        ...deserializeConditionalColoringExpression(resource.expression, entityName, resource.customFieldId)
    };
}
/** Deserializes one rule without allowing malformed optional rule data to fail its parent Plan. */ export function tryDeserializeConditionalColoringRule(resource, entityName) {
    try {
        return deserializeConditionalColoringRule(resource, entityName);
    } catch  {
        return undefined;
    }
}
/** Serializes ordered client rules into create resources. */ export function serializeConditionalColoringRulesForCreate(rules) {
    // Rule order is client-owned; sort explicitly because service collections do not guarantee ordering.
    const ordered = [
        ...rules
    ].sort((left, right)=>left.order - right.order);
    let previousHint = null;
    return ordered.map((rule)=>{
        const orderHint = generateChainedOrderHint(previousHint, null);
        // Resolve locally (mirroring the service) so the next rule brackets after this one.
        previousHint = calculateOrderHintFromExpression(orderHint);
        return serializeConditionalColoringRuleForCreate(rule, orderHint);
    });
}
/** Converts a client rule to the full resource shape used by Plan persistence. */ export function serializeConditionalColoringRuleForPersistence(rule) {
    return {
        id: rule.id,
        "@etag": rule.version ?? "",
        customFieldId: getConditionalColoringCustomFieldId(rule),
        expression: serializeConditionalColoringRuleExpression(rule),
        orderHint: rule.orderHint ?? ""
    };
}
/** Builds create, update, and delete delta entries for changed rules. */ export function serializeConditionalColoringRulesForUpdate(currentRules, sourceRules) {
    const result = [];
    const currentIds = new Set(currentRules.map((rule)=>rule.id));
    for (const sourceRule of sourceRules){
        if (!currentIds.has(sourceRule.id) && sourceRule.version) {
            result.push({
                "@removed": {},
                "@id": sourceRule.id,
                "@etag": sourceRule.version
            });
        }
    }
    // Rule order is client-owned; sort explicitly because service collections do not guarantee ordering.
    const ordered = [
        ...currentRules
    ].sort((left, right)=>left.order - right.order);
    let previousHint = null;
    ordered.forEach((currentRule, index)=>{
        const sourceRule = sourceRules.find((rule)=>rule.id === currentRule.id);
        // New rule: bracket after the previous rule and before the next resolved neighbour, chaining a
        // run of new rules so their resolved hints preserve order.
        if (!sourceRule?.version) {
            const orderHint = generateChainedOrderHint(previousHint, findNextConditionalColoringOrderHint(ordered, index));
            previousHint = calculateOrderHintFromExpression(orderHint);
            result.push(serializeConditionalColoringRuleForCreate(currentRule, orderHint));
            return;
        }
        const moved = currentRule.order !== sourceRule.order;
        if (isEmpty(getDiff(sourceRule, currentRule, undefined, true))) {
            // Unchanged existing rule still anchors subsequent new rules.
            previousHint = currentRule.orderHint ?? previousHint;
            return;
        }
        const currentCustomFieldId = getConditionalColoringCustomFieldId(currentRule);
        const sourceCustomFieldId = getConditionalColoringCustomFieldId(sourceRule);
        const update = {
            "@id": currentRule.id,
            "@etag": currentRule.version ?? sourceRule.version,
            // if the current rule has a custom field id, use it. If not, and the source rule had a custom field id, set it to null to clear it. Otherwise, leave it undefined to not change it.
            customFieldId: currentCustomFieldId ?? (sourceCustomFieldId ? null : undefined),
            expression: serializeConditionalColoringRuleExpression(currentRule)
        };
        // Only re-position the rule (send an order hint) when its evaluation order actually changed.
        if (moved) {
            const orderHint = generateChainedOrderHint(previousHint, findNextConditionalColoringOrderHint(ordered, index));
            update.orderHint = orderHint;
            previousHint = calculateOrderHintFromExpression(orderHint);
        } else {
            previousHint = currentRule.orderHint ?? previousHint;
        }
        result.push(update);
    });
    return result.length > 0 ? result : undefined;
}
/** Applies rule deltas, returning updated rules or `undefined` for a no-op. */ export function applyConditionalColoringRulesDelta(currentRules, deltaRules, entityName) {
    if (deltaRules.length === 0) {
        return undefined;
    }
    const rulesById = new Map(currentRules.map((rule)=>[
            rule.id,
            rule
        ]));
    let hasChanges = false;
    for (const deltaRule of deltaRules){
        if ("@removed" in deltaRule) {
            hasChanges = rulesById.delete(deltaRule["@id"]) || hasChanges;
            continue;
        }
        const existingRule = rulesById.get(deltaRule.id);
        if (existingRule) {
            let updatedRuleFields = {};
            // If expression is defined, deserialize it.
            if (deltaRule.expression !== undefined) {
                updatedRuleFields = deserializeConditionalColoringExpression(deltaRule.expression, entityName, deltaRule.customFieldId);
            } else if (deltaRule.customFieldId === null) {
                updatedRuleFields = {
                    customFieldId: undefined,
                    columnId: undefined
                };
            } else if (deltaRule.customFieldId !== undefined) {
                updatedRuleFields = {
                    customFieldId: deltaRule.customFieldId,
                    columnId: toCustomFieldColumnId(deltaRule.customFieldId)
                };
            }
            rulesById.set(deltaRule.id, {
                ...existingRule,
                ...updatedRuleFields,
                version: deltaRule["@etag"] ?? existingRule.version,
                orderHint: deltaRule.orderHint ?? existingRule.orderHint
            });
        } else {
            if (deltaRule.expression === undefined) {
                throw createConditionalColoringDeserializationError(entityName, "Expression");
            }
            if (deltaRule.orderHint === undefined) {
                throw createConditionalColoringDeserializationError(entityName, "OrderHint");
            }
            rulesById.set(deltaRule.id, deserializeConditionalColoringRule({
                ...deltaRule,
                expression: deltaRule.expression,
                orderHint: deltaRule.orderHint
            }, entityName));
        }
        hasChanges = true;
    }
    // Rule order is client-owned; sort explicitly because service collections do not guarantee ordering.
    return hasChanges ? Array.from(rulesById.values()).sort((left, right)=>left.order - right.order) : undefined;
}
/** Parses and validates a client-owned rule expression blob. */ function deserializeConditionalColoringExpression(expression, entityName, serviceCustomFieldId) {
    try {
        const parsedExpression = JSON.parse(expression);
        if (!isConditionalColoringExpression(parsedExpression)) {
            throw new Error("Invalid conditional coloring expression.");
        }
        let customFieldId = parsedExpression.customFieldId;
        if (serviceCustomFieldId === null) {
            customFieldId = undefined;
        } else if (serviceCustomFieldId !== undefined) {
            customFieldId = serviceCustomFieldId;
        }
        return {
            ...parsedExpression,
            customFieldId,
            columnId: customFieldId ? toCustomFieldColumnId(customFieldId) : parsedExpression.columnId
        };
    } catch  {
        throw createConditionalColoringDeserializationError(entityName, "Expression");
    }
}
/** Creates the appropriate error for a malformed conditional-coloring resource field. */ function createConditionalColoringDeserializationError(entityName, key) {
    if (key === "Expression") {
        return new Error("Failed to deserialize the expression");
    }
    return new Error(`Deserialization Error: [Entity=${entityName}][Field=ConditionalColoringRules][Key=${key}]`);
}
/** Checks whether an unknown value is a valid serialized conditional-coloring expression. */ function isConditionalColoringExpression(value) {
    if (!isRecord(value)) {
        return false;
    }
    const expression = value.expression;
    if (!isRecord(expression)) {
        return false;
    }
    const firstValue = expression.firstValue;
    const secondValue = expression.secondValue;
    const hasValidFirstValue = typeof firstValue === "string" || typeof firstValue === "number" || Array.isArray(firstValue) && firstValue.every((item)=>typeof item === "string");
    const hasValidSecondValueIfPresent = secondValue === undefined || typeof secondValue === "string" || typeof secondValue === "number";
    return (value.customFieldId === undefined || typeof value.customFieldId === "string") && isConditionalColoringColumnId(value.columnId) && typeof value.colorIndex === "number" && typeof value.order === "number" && isConditionalColoringOperator(expression.operator) && hasValidFirstValue && hasValidSecondValueIfPresent;
}
/** Checks whether an unknown value is a non-array object. */ function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** Serializes the client-owned rule fields into the opaque expression blob. */ function serializeConditionalColoringRuleExpression(rule) {
    return JSON.stringify({
        customFieldId: getConditionalColoringCustomFieldId(rule),
        columnId: rule.columnId,
        colorIndex: rule.colorIndex,
        expression: rule.expression,
        order: rule.order
    });
}
/** Generates an order hint after the previous rule and before the next rule when possible. */ function generateChainedOrderHint(previousHint, nextHint) {
    if (previousHint && nextHint && previousHint >= nextHint) {
        return generateOrderHintBetween(previousHint, null);
    }
    return generateOrderHintBetween(previousHint, nextHint);
}
/** Finds the next existing rule order hint after the provided index. */ function findNextConditionalColoringOrderHint(orderedRules, index) {
    for(let next = index + 1; next < orderedRules.length; next++){
        const hint = orderedRules[next].orderHint;
        if (hint) {
            return hint;
        }
    }
    return null;
}
/** Converts a client rule to the service resource shape used when creating a rule. */ function serializeConditionalColoringRuleForCreate(rule, orderHint) {
    return {
        customFieldId: getConditionalColoringCustomFieldId(rule),
        expression: serializeConditionalColoringRuleExpression(rule),
        orderHint
    };
}
/** Gets a rule's custom-field ID, deriving it from a custom-field column ID when needed. */ function getConditionalColoringCustomFieldId(rule) {
    return rule.customFieldId ?? (rule.columnId && isCustomFieldColumn(rule.columnId) ? getCustomFieldDefinitionId(rule.columnId) : undefined);
}
